'use server';

import { google } from 'googleapis';
import { v4 as uuidv4 } from 'uuid';
import { Readable } from 'stream';

type FileType = 'webp' | 'pdf';

export async function uploadFileAction(
  uploadType: 'CDN' | 'GDRIVE' = 'CDN',
  formData: FormData,
  fileType: FileType
) {
  try {
    if (uploadType === 'CDN') {
      return await uploadFileToBunnyCdn(formData, fileType);
    } else if (uploadType === 'GDRIVE') {
      return await uploadFileToGoogleDrive(formData, fileType);
    } else {
      return { error: 'Invalid upload type', status: 400 };
    }
  } catch (_error) {
    return { error: 'Internal server error', status: 500 };
  }
}

export async function uploadFileToBunnyCdn(
  formData: FormData,
  fileType: FileType
) {
  const CDN_BASE_UPLOAD_URL = process.env.CDN_BASE_UPLOAD_URL!;
  const CDN_BASE_ACCESS_URL = process.env.CDN_BASE_ACCESS_URL!;
  const CDN_API_KEY = process.env.CDN_API_KEY!;

  const file = formData.get('file') as File;
  const uniqueFileName = formData.get('uniqueFileName') || uuidv4(); // Generate unique key if not provided

  if (!file) {
    return { error: 'File is required', status: 400 };
  }
  const uploadUrl = `${CDN_BASE_UPLOAD_URL}/${uniqueFileName}.${fileType}`;
  const fileBuffer = Buffer.from(await file.arrayBuffer());

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      AccessKey: CDN_API_KEY,
      'Content-Type': 'application/octet-stream',
    },
    body: fileBuffer,
  });
  if (response.ok) {
    return {
      message: 'File uploaded successfully',
      url: `${CDN_BASE_ACCESS_URL}/${uniqueFileName}.${fileType}`,
    };
  } else {
    return { error: 'Failed to upload file', status: response.status };
  }
}

async function uploadFileToGoogleDrive(formData: FormData, fileType: FileType) {
  const drive = createGDriveClient();
  const resumesFolderId = process.env.GOOGLE_DRIVE_RESUMES_FOLDER_ID!;
  const imagesFolderId = process.env.GOOGLE_DRIVE_IMAGES_FOLDER_ID!;
  const uniqueFileName = formData.get('uniqueFileName') || uuidv4(); // Generate unique key if not provided

  const file = formData.get('file') as File;

  if (!file) {
    return { error: 'File is required', status: 400 };
  }

  // Convert File to Buffer
  const arrayBuffer = await file.arrayBuffer();
  const buffer = Buffer.from(arrayBuffer);

  // Create a readable stream from the buffer
  const readable = new Readable();
  readable.push(buffer);
  readable.push(null); // Signal the end of the stream

  const response = await drive.files.create({
    requestBody: {
      name: `${uniqueFileName}.${fileType}`,
      mimeType: fileType === 'webp' ? 'image/webp' : 'application/pdf',
      parents: [fileType === 'webp' ? imagesFolderId : resumesFolderId],
    },
    media: {
      mimeType: fileType === 'webp' ? 'image/webp' : 'application/pdf',
      body: readable,
    },
  });

  if (response.data) {
    // Make the file publicly accessible
    await drive.permissions.create({
      fileId: response.data.id as string,
      requestBody: {
        role: 'reader',
        type: 'anyone',
      },
    });

    return {
      message: 'File uploaded successfully',
      url:
        fileType === 'webp'
          ? `https://drive.google.com/thumbnail?id=${response.data.id}&sz=w1000`
          : `https://drive.google.com/uc?export=view&id=${response.data.id}`,
    };
  } else {
    return { error: 'Failed to upload file', status: 400 };
  }
}

export async function moveFilesToArchiveFolder(fileId: string) {
  const drive = createGDriveClient();
  const resumesFolderId = process.env.GOOGLE_DRIVE_RESUMES_FOLDER_ID!;
  const archiveResumesFolderId =
    process.env.GOOGLE_DRIVE_ARCHIVE_RESUMES_FOLDER_ID!;
  try {
    /**
     * Permissions list Response
     * {
      permissions: [
        { id: 'anyoneWithLink', type: 'anyone', role: 'reader' },
        { id: '162233xxxxx4959484', type: 'user', role: 'writer' },
        { id: '058062xxxxxxx74464', type: 'user', role: 'owner' }
      ]
    }
     */

    await Promise.all([
      drive.files.update({
        fileId: fileId,
        addParents: archiveResumesFolderId,
        removeParents: resumesFolderId,
      }),
      drive.permissions.delete({
        fileId: fileId,
        permissionId: 'anyoneWithLink',
      }),
    ]);

    return { message: 'Files moved successfully', status: 200 };
  } catch (_error) {
    return { error: 'Internal server error', status: 500 };
  }
}

function createGDriveClient() {
  // Create a JWT client using the service account credentials
  const auth = new google.auth.JWT(
    process.env.GOOGLE_SERVICE_ACCOUNT_EMAIL,
    undefined,
    process.env.GOOGLE_PRIVATE_KEY?.replace(/\\n/g, '\n'),
    ['https://www.googleapis.com/auth/drive']
  );

  const drive = google.drive({ version: 'v3', auth });
  return drive;
}

/**
Let me explain what you need to do to fix the Google Drive authentication:

You need to create a Google Cloud service account and get its credentials. Here's how to set up your environment variables:
Add these variables to your .env file:

CopyInsert
# Google Drive API credentials
GOOGLE_SERVICE_ACCOUNT_EMAIL=your-service-account@your-project.iam.gserviceaccount.com
GOOGLE_PRIVATE_KEY="-----BEGIN PRIVATE KEY-----\nYour private key content here\n-----END PRIVATE KEY-----\n"
GOOGLE_DRIVE_FOLDER_ID=1_JNu-krSpMUIfDz9VTd2CS00T1XZzdxV
To get these credentials:

Go to the Google Cloud Console
Create a new project or select an existing one
Enable the Google Drive API
Go to "Credentials" and create a service account
Create a key for that service account (JSON format)
Open the downloaded JSON file and copy the client_email value to GOOGLE_SERVICE_ACCOUNT_EMAIL
Copy the private_key value to GOOGLE_PRIVATE_KEY
Make sure the service account has access to the Google Drive folder you're using
After adding these credentials to your .env file, restart your development server and the file upload should work correctly.

Would you like me to suggest an alternative file upload solution that might be easier to implement, like using Cloudinary or Firebase Storage?
*/
